/* eslint-disable valid-jsdoc */
import React, {
  createContext, useState, useContext, useCallback, useMemo, ReactNode, ComponentType, FC,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import {
  ICounterContextProviderProps,
  IGenerateCounterOptions,
  IPromiseCounterActionContext,
  IPromiseCounterContext,
  IPromiseCounterScopedContext,
  IPromiseCounterTotalContext,
} from './types';

const defaultContextValue: IPromiseCounterContext = Object.freeze({
  count: 0,
});

const defaultScopedContextValue: IPromiseCounterScopedContext = Object.freeze({
  scopedCount: 0,
});

const defaultTotalContextValue: IPromiseCounterTotalContext = Object.freeze({
  totalCount: defaultContextValue.count + defaultScopedContextValue.scopedCount,
});

const defaultActionContextValue: IPromiseCounterActionContext = Object.freeze({
  increment: _.noop,
  incrementScoped: _.noop,
  decrement: _.noop,
  decrementScoped: _.noop,
});

/**
 * Generate a Context Provider Component for the specified
 *    CounterContext, CounterScopedContext, CounterTotalContext and CounterActionContext.
 * The CounterContext provides:
 *    count: the increment count coming from the current Context.
 * The CounterScopedContext provides:
 *    scopedCount: the scoped increment count coming from all the scopes of child Contextes.
 * The CounterTotalContext provides:
 *    totalCount: the sum of count and scopedCount.
 * The CounterActionContext provides:
 *    increment: the increment function that will add 1 to count,
 *               which will also propagate the addition to parent context's scopedCount
 *               by calling parent context's incrementScoped function.
 *    incrementScoped: the scoped increment function that will add 1 to scopedCount,
 *               which will also propagate the addition to parent context's scopedCount
 *               by calling parent context's incrementScoped function.
 *    decrement: the decrement function that will subtract 1 from count,
 *               which will also propagate the subtraction to parent context's scopedCount
 *               by calling parent context's decrementScoped function.
 *    decrementScoped: the scoped decrement function that will subtract 1 from scopedCount,
 *               which will also propagate the subtraction to parent context's scopedCount
 *               by calling parent context's decrementScoped function.
 * @param {React.Context<IPromiseCounterContext>} CounterContext - The CounterContext object that the Provider is generated for.
 * @param {React.Context<IPromiseCounterScopedContext>} CounterScopedContext - The CounterScopedContext object that the Provider is generated for.
 * @param {React.Context<IPromiseCounterTotalContext>} CounterTotalContext - The CounterTotalContext object that the Provider is generated for.
 * @param {React.Context<IPromiseCounterActionContext>} CounterActionContext - The CounterActionContext object that the Provider is generated for.
 * @param {IGenerateCounterOptions} options - The options that can be configured.
 * @param {string} options.name - The Counter name used for formatting Provider displayName.
 * @returns {React.FC<ICounterContextProviderProps>} - The generated CounterContext Provider.
 */
export const generateCounterContextProvider = (
  CounterContext: React.Context<IPromiseCounterContext>,
  CounterScopedContext: React.Context<IPromiseCounterScopedContext>,
  CounterTotalContext: React.Context<IPromiseCounterTotalContext>,
  CounterActionContext: React.Context<IPromiseCounterActionContext>,
  { name }: IGenerateCounterOptions
): FC<ICounterContextProviderProps> => {
  const Provider: FC<ICounterContextProviderProps> = ({ children }: ICounterContextProviderProps) => {
    const {
      incrementScoped: incrementScopedUpper,
      decrementScoped: decrementScopedUppper,
    } = useContext(CounterActionContext);

    // Counter counts
    const [count, setCount] = useState(0);
    const [scopedCount, setScopedCount] = useState(0);
    const totalCount = count + scopedCount;

    // Counter actions
    const increment = useCallback(() => {
      setCount((prevCount: number) => prevCount + 1);
      incrementScopedUpper();
    }, [incrementScopedUpper]);
    const decrement = useCallback(() => {
      setCount((prevCount: number) => prevCount - 1);
      decrementScopedUppper();
    }, [decrementScopedUppper]);
    const incrementScoped = useCallback(() => {
      setScopedCount((prevCount: number) => prevCount + 1);
      incrementScopedUpper();
    }, [incrementScopedUpper]);
    const decrementScoped = useCallback(() => {
      setScopedCount((prevCount: number) => prevCount - 1);
      decrementScopedUppper();
    }, [decrementScopedUppper]);

    // Memoize different context values
    const memoCount = useMemo(() => ({ count }), [count]);
    const memoScopedCount = useMemo(() => ({ scopedCount }), [scopedCount]);
    const memoTotalCount = useMemo(() => ({ totalCount }), [totalCount]);
    const memoActions = useMemo(() => ({
      increment,
      incrementScoped,
      decrement,
      decrementScoped,
    }), [decrement, decrementScoped, increment, incrementScoped]);

    // Split the Contextes for perf optimization, for usage, only use the Context that is needed
    return (
      <CounterContext.Provider value={memoCount}>
        <CounterScopedContext.Provider value={memoScopedCount}>
          <CounterTotalContext.Provider value={memoTotalCount}>
            <CounterActionContext.Provider value={memoActions}>
              {children}
            </CounterActionContext.Provider>
          </CounterTotalContext.Provider>
        </CounterScopedContext.Provider>
      </CounterContext.Provider>
    );
  };

  Provider.displayName = `${name}CounterContextProvider`;
  Provider.propTypes = {
    children: PropTypes.node.isRequired,
  };

  return Provider;
};

/**
 * A simple hook for using the value of the specified CounterContext.
 * @param {React.Context<T>} CounterContext - The CounterContext object that the hook is generated for.
 * @returns {() => T} - The generated CounterContext hook.
 */
export function generateCounterContextHook<T>(CounterContext: React.Context<T>): (() => T) {
  return () => useContext(CounterContext);
}

/**
 * Generate the HOC to wrap Component with a specified CounterContext Provider.
 * @param {React.ComponentType<{ children: React.ReactNode }>} CounterContextProvider - The specified CounterContext Provider.
 * @returns {(WrappedComponent: React.ComponentType<P>) => React.FC<P>} - The HOC.
 */
export const generateCounterContextProviderHOC = (CounterContextProvider: ComponentType<{ children: ReactNode }>) => <P extends object>(WrappedComponent: ComponentType<P>): FC<P> => {
  const HOC: FC<P> = (props: P) => (
    <CounterContextProvider>
      <WrappedComponent {...props} />
    </CounterContextProvider>
  );

  HOC.displayName = `With${getDisplayName(CounterContextProvider)}(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HOC, WrappedComponent);

  return HOC;
};

/**
 * Generate a set of utils for a Counter.
 * @param {IGenerateCounterOptions} options - The options that can be configured.
 * @param {string} options.name - The Counter name.
 * @returns {object} - The Counter utils:
 *    useCounterContext: the simple hook for using the value of the CounterContext.
 *    useCounterScopedContext: the simple hook for using the value of the CounterScopedContext.
 *    useCounterTotalContext: the simple hook for using the value of the CounterTotalContext.
 *    useCounterActionContext: the simple hook for using the value of the CounterActionContext.
 *    CounterContextProvider: the Provider Component for
 *        CounterContext, CounterScopedContext, CounterTotalContext and CounterActionContext.
 *    withCounterContextProvider: the HOC to wrap Component with CounterContextProvider.
 */
export const generateCounter = ({ name }: IGenerateCounterOptions) => {
  const CounterContext = createContext <IPromiseCounterContext>(defaultContextValue);
  const CounterScopedContext = createContext <IPromiseCounterScopedContext>(defaultScopedContextValue);
  const CounterTotalContext = createContext <IPromiseCounterTotalContext>(defaultTotalContextValue);
  const CounterActionContext = createContext <IPromiseCounterActionContext>(defaultActionContextValue);
  const useCounterContext = generateCounterContextHook(CounterContext);
  const useCounterScopedContext = generateCounterContextHook(CounterScopedContext);
  const useCounterTotalContext = generateCounterContextHook(CounterTotalContext);
  const useCounterActionContext = generateCounterContextHook(CounterActionContext);
  const CounterContextProvider = generateCounterContextProvider(
    CounterContext,
    CounterScopedContext,
    CounterTotalContext,
    CounterActionContext,
    { name }
  );
  const withCounterContextProvider = generateCounterContextProviderHOC(CounterContextProvider);

  return {
    useCounterContext,
    useCounterScopedContext,
    useCounterTotalContext,
    useCounterActionContext,
    CounterContextProvider,
    withCounterContextProvider,
  };
};
